import React, { Fragment, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import Navbar from "../../components/Navbar/Navbar";
import PageTitle from "../../components/pagetitle/PageTitle";
import Footer from "../../components/footer/Footer";
import Scrollbar from "../../components/scrollbar/scrollbar";
import CursorMaus from "../../components/CursorMaus/CursorMaus";
import Logo from "../../images/logo.webp";
import Phone from "../../images/telephone.svg";
import { getBlogBySlug } from "../../api/blogs";
import BookNowModal from "../../components/BookNowModal/BookNowModal";
import "./BlogSinglePage.scss";

const BASE_URL = "https://cashflowsummit.in";

const renderTextWithLinks = (text, links = []) => {
  if (!links.length) return text;

  let parts = [text];

  links.forEach((link, linkIndex) => {
    if (!link?.text) return;

    parts = parts.flatMap((part) => {
      if (typeof part !== "string") return [part];

      const split = part.split(link.text);

      return split.flatMap((segment, index) =>
        index < split.length - 1
          ? [
              segment,
              <a
                key={`inline-${linkIndex}-${index}`}
                href={link.to}
                className="blog-inline-link"
              >
                {link.text}
              </a>,
            ]
          : [segment]
      );
    });
  });

  return parts;
};

const renderContentBlock = (block, i, openBookNow) => {
  switch (block.type) {
    case "p":
      return (
        <p key={i} className={block.className}>
          {renderTextWithLinks(block.text, block.links)}
        </p>
      );
    case "h3":
      return <h3 key={i}>{block.text}</h3>;
    case "h4":
      return <h4 key={i}>{block.text}</h4>;
    case "ul":
      return (
        <ul key={i}>
          {block.items.map((item, j) => (
            <li key={j}>{item}</li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol key={i}>
          {block.items.map((item, j) => (
            <li key={j}>{item}</li>
          ))}
        </ol>
      );
    case "faq":
      return (
        <div key={i} className="blog-faq">
          {block.items.map((item, j) => (
            <div key={j} className="faq-item">
              <p className="faq-q">{item.q}</p>
              <p className="faq-a">{item.a}</p>
            </div>
          ))}
        </div>
      );
    case "cta":
      return (
        <p key={i} className="blog-cta">
          <Link to={block.to || "/contact"} onClick={() => window.scrollTo(10, 0)}>
            {block.text}
          </Link>
        </p>
      );
    case "ctaExternal":
      const isSameSiteCta =
        typeof block.href === "string" &&
        block.href.startsWith("https://cashflowsummit.in");
      return (
        <p key={i} className="blog-cta">
          <a
            href={block.href}
            {...(!isSameSiteCta
              ? { target: "_blank", rel: "noopener noreferrer" }
              : {})}
          >
            {block.text}
          </a>
        </p>
      );
    case "ctaModal":
      return (
        <p key={i} className="blog-cta">
          <button
            type="button"
            className="theme-btn"
            onClick={() => openBookNow?.()}
          >
            {block.text}
          </button>
        </p>
      );
    default:
      return null;
  }
};

const BlogSinglePage = () => {
  const { slug } = useParams();
  const post = getBlogBySlug(slug);
  const [isBookNowOpen, setIsBookNowOpen] = useState(false);
  const canonicalUrl = post
    ? post.canonical?.startsWith("http")
      ? post.canonical
      : `${BASE_URL}${post.canonical || `/blog/${post.slug}`}`
    : `${BASE_URL}/blog/${slug}`;

  const ClickHandler = () => {
    window.scrollTo(10, 0);
  };

  if (!post) {
    return (
      <Fragment>
        <Navbar Logo={Logo} hclass={"wpo-site-header s3"} telephone={Phone} />
        <PageTitle pageTitle={"Not found"} pagesub={"Blog"} />
        <section className="section-padding">
          <div className="container text-center">
            <p>This article could not be found.</p>
            <Link className="theme-btn" to="/blog" onClick={ClickHandler}>
              Back to blog
            </Link>
          </div>
        </section>
        <Footer />
        <Scrollbar />
        <CursorMaus />
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Helmet>
        <title>{post.seoTitle || `${post.title} | Cash Flow Summit 2026`}</title>
        <meta name="description" content={post.seoDescription || post.description} />
        <meta
          name="keywords"
          content={post.seoKeywords || `${post.catagory}, cash flow, Cash Flow Summit 2026`}
        />
        <meta name="author" content="Jagmohan Singh" />
        <meta name="robots" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:url" content={canonicalUrl} />
      </Helmet>
      <Navbar Logo={Logo} hclass={"wpo-site-header s3"} telephone={Phone} />
      <PageTitle
        pageTitle={post.breadcrumbTitle || post.title}
        pagesub={"Blog"}
      />
      <section className="wpo-blog-single-section section-padding">
        <div className="container">
          <div className="row">
            <div className="col col-lg-10 offset-lg-1">
              <div className="wpo-blog-content">
                <div className="post format-standard-image">
                  <div className="entry-meta">
                    <ul>
                      <li>
                        <i className="ti-user"></i>
                        By <span>{post.name}</span>
                      </li>
                      <li>
                        <i className="ti-calendar"></i>
                        {post.create_at}
                      </li>
                      <li>
                        <i className="ti-tag"></i>
                        {post.catagory}
                      </li>
                    </ul>
                  </div>
                  <div className="entry-media">
                    <img src={post.blogSingleImg} alt={post.title} />
                  </div>
                  <h2>{post.title}</h2>
                  <div className="blog-body">
                    {post.blocks
                      ? post.blocks.map((block, i) =>
                          renderContentBlock(block, i, () =>
                            setIsBookNowOpen(true)
                          )
                        )
                      : (post.paragraphs || []).map((p, i) => (
                          <p key={i}>{p}</p>
                        ))}
                  </div>
                  <BookNowModal
                    isOpen={isBookNowOpen}
                    onClose={() => setIsBookNowOpen(false)}
                    sourcePage={`blog-${slug}`}
                  />
                  <div className="entry-bottom mt-4">
                    <Link
                      to="/blog"
                      className="theme-btn"
                      onClick={ClickHandler}
                    >
                      <i className="ti-arrow-left"></i> All articles
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
      <Scrollbar />
      <CursorMaus />
    </Fragment>
  );
};

export default BlogSinglePage;
